/**
 * lib/attendanceCheck.ts
 * ------------------------------------------------------------------
 * IS THIS CHILD STILL COMING TO SCHOOL?
 *
 * The one question the fees office cannot answer from its own records, and the
 * reason someone walks to a classroom to read the paper register before a debt
 * letter goes out. A family that owes P18,000 and left in June is a write-off
 * decision; a family that owes P18,000 whose child was in class this morning is
 * a conversation. The ledger cannot tell them apart.
 *
 * WHAT THIS IS NOT. It is not an attendance report and must not grow into one.
 * It answers a yes/no with the evidence for it — last seen, how many of the
 * recent school days, and the run of days since. Absence patterns, lateness
 * trends and interventions belong to the attendance module and to the people
 * trained to read them.
 *
 * NO REGISTER IS NOT AN ABSENCE. A day nobody marked is reported as its own
 * thing. Counting unmarked days as absences would turn a class whose teacher
 * was away into a room full of children who have left the school — the exact
 * false positive that would make the office stop trusting this.
 *
 * BUT OFF THE REGISTER IS NOT NOTHING EITHER, and this is the distinction the
 * whole feature turns on. A pupil who leaves does not start being marked absent
 * — they simply stop appearing on the register at all. Treating "the class was
 * marked and this child was not on it" as no evidence would report a pupil who
 * left in July as attending, on the strength of their last present day, which is
 * precisely the wrong answer for the only question being asked. So a day the
 * class WAS marked and the pupil was not listed counts as OFF-ROLL, and a run of
 * those at the recent end is the strongest signal available that the child has
 * gone. Off-roll days at the OLD end are discarded instead: they mean the pupil
 * joined this class part-way through the window, not that they left it.
 *
 * PURE. No Firestore, no React.
 * ------------------------------------------------------------------ */

export type Mark = 'present' | 'absent' | 'late' | 'excused';

/** One class's register for one day, as stored in `reportDailyAttendance`. */
export interface DailyRegister {
  date: string;
  entries: Record<string, string | undefined>;
}

export type DayState = 'present' | 'late' | 'absent' | 'excused' | 'off-roll' | 'no-register';

export interface AttendanceDay {
  date: string;
  state: DayState;
}

export type AttendanceVerdict = 'attending' | 'patchy' | 'not-seen' | 'off-roll' | 'unknown';

export interface AttendanceCheck {
  /** Most recent day the pupil was physically in school, or null. */
  lastSeen: string | null;
  /** School days WITH a register since that day. Null when never seen. */
  schoolDaysSinceSeen: number | null;
  /** Days that carry a register, in the window examined. */
  daysMarked: number;
  daysPresent: number;
  daysAbsent: number;
  /** Of the marked days, the share the pupil was in school. Null if none. */
  presentRate: number | null;
  /** Consecutive marked days absent, counting back from the latest register. */
  currentAbsentRun: number;
  /** Consecutive most-recent days the class was marked and the pupil was not on
   *  it. The signal that a pupil has left rather than is away. */
  offRollRun: number;
  /** Newest first — for a strip of dots on screen. */
  days: AttendanceDay[];
  verdict: AttendanceVerdict;
  /** One sentence a bursar can act on, or quote in a letter. */
  summary: string;
}

const IN_SCHOOL = new Set<DayState>(['present', 'late']);

const normalise = (v: string | undefined): DayState => {
  const s = String(v ?? '').toLowerCase();
  if (s === 'present' || s === 'p') return 'present';
  if (s === 'late' || s === 'l') return 'late';
  if (s === 'excused' || s === 'e') return 'excused';
  if (s === 'absent' || s === 'a') return 'absent';
  return 'no-register';
};

const dmy = (iso: string): string => {
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

/**
 * Summarise one pupil's recent attendance from their class's registers.
 *
 * `registers` may arrive in any order and may include days before the pupil
 * joined the class; only days whose register mentions the pupil are counted, so
 * a mid-term transfer does not read as a term of absence.
 */
export function checkAttendance(studentId: string, registers: DailyRegister[]): AttendanceCheck {
  const all: AttendanceDay[] = registers
    .filter((r) => r?.date && r.entries && Object.keys(r.entries).length > 0)
    .map((r) => ({
      date: r.date,
      // Present in the register's entries → their mark. Absent from the entries
      // altogether, on a day the class WAS marked → off the roll.
      state: r.entries[studentId] === undefined ? 'off-roll' as DayState : normalise(r.entries[studentId]),
    }))
    .sort((a, b) => b.date.localeCompare(a.date));

  // Drop off-roll days at the OLD end — the pupil had not joined this class yet.
  let cut = all.length;
  while (cut > 0 && all[cut - 1].state === 'off-roll') cut -= 1;
  const days = all.slice(0, cut);

  let offRollRun = 0;
  for (const d of days) {
    if (d.state !== 'off-roll') break;
    offRollRun += 1;
  }

  const onRoll = days.filter((d) => d.state !== 'off-roll');
  const daysMarked = onRoll.length;
  const daysPresent = onRoll.filter((d) => IN_SCHOOL.has(d.state)).length;
  const daysAbsent = onRoll.filter((d) => d.state === 'absent').length;
  const lastSeen = days.find((d) => IN_SCHOOL.has(d.state))?.date ?? null;

  let currentAbsentRun = 0;
  for (const d of days) {
    if (d.state === 'off-roll') continue;
    if (IN_SCHOOL.has(d.state)) break;
    if (d.state === 'absent' || d.state === 'excused') currentAbsentRun += 1;
  }

  const schoolDaysSinceSeen = lastSeen === null
    ? null
    : days.filter((d) => d.date > lastSeen).length;

  const presentRate = daysMarked ? daysPresent / daysMarked : null;

  let verdict: AttendanceVerdict;
  if (!days.length) verdict = 'unknown';
  // Two is enough. One missing day is a register taken in a hurry; two in a row
  // at the recent end, when the class was marked both times, is a pupil who is
  // no longer on it — and this only ever prompts someone to CHECK enrolment.
  else if (offRollRun >= 2) verdict = 'off-roll';
  else if (!daysMarked) verdict = 'unknown';
  // Five consecutive school days is the point at which a school stops assuming
  // illness — the same threshold the attendance module treats as a concern.
  else if (!lastSeen || currentAbsentRun >= 5) verdict = 'not-seen';
  else if ((presentRate ?? 0) < 0.6) verdict = 'patchy';
  else verdict = 'attending';

  const pct = presentRate === null ? '' : `${Math.round(presentRate * 100)}%`;
  const summary = verdict === 'unknown'
    ? (days.length
      ? 'The class register has been taken, but never with this pupil on it in the period checked.'
      : 'No register has been taken for this class in the period checked — nothing can be said either way.')
    : verdict === 'off-roll'
      ? `No longer on the class register. The last ${offRollRun} register${offRollRun === 1 ? '' : 's'} for this class do not list this pupil`
        + (lastSeen ? `, who was last in school on ${dmy(lastSeen)}. ` : '. ')
        + 'Confirm whether the pupil has left before sending a demand.'
      : verdict === 'not-seen'
        ? (lastSeen
          ? `Not in school since ${dmy(lastSeen)} — ${currentAbsentRun} school day${currentAbsentRun === 1 ? '' : 's'} running. Confirm the pupil has not left before sending a demand.`
          : 'Never marked present in the period checked. Confirm the pupil is still enrolled before sending a demand.')
        : verdict === 'patchy'
          ? `In school ${daysPresent} of ${daysMarked} marked days (${pct}). Last seen ${dmy(lastSeen as string)}.`
          : `Attending — in school ${daysPresent} of ${daysMarked} marked days (${pct}). Last seen ${dmy(lastSeen as string)}.`;

  return {
    lastSeen,
    schoolDaysSinceSeen,
    daysMarked,
    daysPresent,
    daysAbsent,
    presentRate,
    currentAbsentRun,
    offRollRun,
    days,
    verdict,
    summary,
  };
}

/** The id range covering one class's registers between two dates. Doc ids are
 *  `{classId}_{yyyy-mm-dd}`, so a name range fetches exactly that class's days
 *  without a composite index and without reading the whole year. */
export function registerIdRange(classId: string, fromISO: string, toISO: string): [string, string] {
  return [`${classId}_${fromISO}`, `${classId}_${toISO}`];
}

/** The window the check looks back over: calendar days, generous enough to hold
 *  roughly six school weeks including a mid-term break. */
export const LOOKBACK_DAYS = 60;

export function lookbackFrom(todayISO: string, days = LOOKBACK_DAYS): string {
  const d = new Date(`${todayISO}T00:00:00`);
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}
